import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useAvailabilityPrompt } from 'src/hooks/availability/useAvailabilityPrompt.hook';

/**
 * Asks an Inactivo seller whether they meant to be away, at the moment they do
 * something that says otherwise — sending a message or moving a lead
 * (RET-1350).
 *
 * The always-on `InactiveUserBanner` covers the passive case and is not
 * enough on its own: a seller worked a full day through it while every new
 * lead went to a teammate (ws 233ab4e4, 2026-08-10). A banner is furniture
 * after ten minutes; an interruption tied to the action the seller just took
 * is not, and it arrives seconds into the mistake rather than hours.
 *
 * Never blocks the action that triggered it — the message is already sent and
 * the lead already moved by the time this renders. Gating those behind a
 * dialog would interrupt a live conversation, which is a worse bug than the
 * one being fixed.
 *
 * Mounted once in `PrivateLayout` beside the banner so it survives navigation,
 * but portalled to `document.body` rather than rendered in place. That is
 * load-bearing, not tidiness: daisyUI gives every `.modal` `position:fixed`,
 * `inset:0` and `z-index:999`, so two open modals are ordered purely by DOM
 * position — and the chat on Pipe and Directory is itself a `Modal.Legacy`
 * rendered inside `{children}`, i.e. after this component. Rendered in place,
 * the prompt would mount *behind* the very chat the seller just sent from —
 * invisible and unclickable on its primary surface — while still burning the
 * episode. The portal puts it last in `body`, above sibling modals.
 */
export default function AvailabilityPrompt() {
  // `ready` gates on the namespace fetch (useSuspense is off app-wide) so a
  // cold cache can never paint raw keys inside a dialog the seller must read.
  const { t, ready } = useTranslation('common/availabilityPrompt');
  const { reason, goActive, stayInactive } = useAvailabilityPrompt();

  if (!ready || !reason) return null;

  return createPortal(
    <Modal.Legacy
      open
      onClickBackdrop={stayInactive}
      className="AvailabilityPrompt rounded-lg p-0 min-w-[29rem] max-w-[29rem]"
    >
      <div className="flex justify-between items-center gap-3 p-4 border-b">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ExclamationTriangleIcon className="size-5 shrink-0 text-warning" />
          {t('title')}
        </h3>
        <button
          type="button"
          onClick={stayInactive}
          aria-label={t('close')}
          data-testid="availability-prompt-close"
        >
          <XMarkIcon className="size-6 cursor-pointer text-neutral-500" />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <p className="text-sm text-base-content/70 text-left">
          {t(`body.${reason}`)}
        </p>

        {/* No spinner or disabled state here on purpose: `goActive` closes this
            dialog in the same React batch that starts the write, so a "saving"
            state could never paint. See the note in useAvailabilityPrompt. */}
        <div className="flex items-center justify-end gap-2">
          <Button type="button" size="sm" color="ghost" onClick={stayInactive}>
            {t('stay')}
          </Button>
          <Button type="button" size="sm" color="primary" onClick={goActive}>
            {t('action')}
          </Button>
        </div>
      </div>
    </Modal.Legacy>,
    document.body,
  );
}
